import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SiteHeader } from './SiteHeader';
import { SiteFooter } from './SiteFooter';
import { useMarqueePause } from './useMarqueePause';

/**
 * Layout wrapper for every public marketing route: header + page content
 * + footer. Mounted from App.tsx around each marketing `<Route>` element.
 *
 * Plain-DOM shell (no Ionic). www is a normal responsive marketing site —
 * the document scrolls, the header uses `position: sticky; top: 0` (in
 * site.css), and there's no need for Ionic's viewport-locked
 * IonPage/IonContent chrome.
 *
 * The `<main id="main">` exists so the skip-link in index.html
 * (`<a class="skip-link" href="#main">`) lands inside the page content,
 * not inside the header.
 *
 * The marquee-pause IntersectionObserver is re-armed on every route
 * change so pages that mount `.marquee` elements (currently HomePage +
 * EatersPage) get the out-of-view-pause behavior without each page
 * having to do anything.
 */
export const SiteLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  useMarqueePause(pathname);

  // Scroll-to-top on route change. React Router's <Switch> keeps this
  // SiteLayout mounted while navigating between marketing routes (same
  // component, same position in the tree, different children prop), so
  // the document's scroll position survives — which feels wrong on a
  // normal website. Instant jump matches how a full-page browser nav
  // would behave.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>
      <SiteHeader />
      <main id="main">{children}</main>
      <SiteFooter />
    </>
  );
};
